import { delegationTokenConfig } from './config';
import { clearOAuthToken } from './oauthToken';
import { getLogger } from '@logging/logger';

interface DelegationTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface StoredToken {
  token: string;
  expiresAt: number;
}

// Callback for when re-authentication is required
type AuthRequiredCallback = () => void;

class DelegationTokenManager {
  private static instance: DelegationTokenManager;
  private tokenCache: StoredToken | null = null;
  private tokenPromise: Promise<string> | null = null;
  private onAuthRequired: AuthRequiredCallback | null = null;

  static getInstance(): DelegationTokenManager {
    if (!DelegationTokenManager.instance) {
      DelegationTokenManager.instance = new DelegationTokenManager();
    }
    return DelegationTokenManager.instance;
  }

  /**
   * Set a callback to be called when OAuth re-authentication is required
   */
  setAuthRequiredCallback(callback: AuthRequiredCallback): void {
    this.onAuthRequired = callback;
  }

  /**
   * Gets a valid delegation token, refreshing if necessary
   */
  async getToken(): Promise<string> {
    // If we have a valid cached token, return it
    if (this.tokenCache && this.isTokenValid(this.tokenCache)) {
      return this.tokenCache.token;
    }

    // If we're already fetching a token, return that promise
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Fetch a new token
    getLogger().debug('delegation token cache miss');
    this.tokenPromise = this.fetchDelegationToken();

    try {
      const token = await this.tokenPromise;
      return token;
    } finally {
      this.tokenPromise = null;
    }
  }

  /**
   * Clears the cached token, forcing a refresh on next request
   */
  clearToken(): void {
    this.tokenCache = null;
    getLogger().info('delegation token cleared');
  }

  private async fetchDelegationToken(): Promise<string> {
    try {
      // Resolve headers (function or object)
      const headers = typeof delegationTokenConfig.headers === 'function' ? delegationTokenConfig.headers() : delegationTokenConfig.headers;

      const response = await fetch(delegationTokenConfig.endpoint, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // OAuth token is invalid or expired - clear it and notify
          getLogger().info('oauth reauth required', { status: response.status });
          clearOAuthToken();
          this.tokenCache = null;
          if (this.onAuthRequired) {
            this.onAuthRequired();
          }
          throw new Error('OAuth token is invalid or expired. Please log in again.');
        }
        throw new Error(`Failed to fetch delegation token: ${response.statusText}`);
      }

      const data: DelegationTokenResponse = await response.json();

      // Cache the token with expiration
      const expiresAt = Date.now() + (data.expires_in - 60) * 1000; // Subtract 60 seconds for buffer
      this.tokenCache = {
        token: data.access_token,
        expiresAt,
      };
      getLogger().debug('delegation token cached', { expiresAt });

      return data.access_token;
    } catch (error) {
      getLogger().error('delegation token fetch failed', {}, error);
      throw new Error('Failed to obtain delegation token');
    }
  }

  private isTokenValid(storedToken: StoredToken): boolean {
    return Date.now() < storedToken.expiresAt;
  }
}

export const delegationTokenManager = DelegationTokenManager.getInstance();
